// eslint-disable  no-console

// C.f.r. Web\Estensa.Web.Rest\Models\Severity.cs
export enum LogMessageType {
    Trace = 0,
    Debug = 1,
    Info = 2,
    Warn = 3,
    Error = 4,
    Fatal = 5,
}

export interface ILogDestination {
    canHandle(type: LogMessageType);

    log(type: LogMessageType, message: any, uid?: string): void;
}

export class ConsoleLogDestination implements ILogDestination {
    canHandle(_) {
        return true;
    }

    log(type: LogMessageType, message: any, _?: string) {
        switch (type) {
            case LogMessageType.Trace:
            case LogMessageType.Debug:
                return console.debug(message);
            case LogMessageType.Warn:
                return console.warn(message);
            case LogMessageType.Error:
            case LogMessageType.Fatal:
                return console.error(message);
            case LogMessageType.Info:
            default:
                return console.log(message);
        }
    }
}

export interface LogLevels {
    trace: boolean;
    debug: boolean;
    info: boolean;
    warn: boolean;
    error: boolean;
    fatal: boolean;
}

export function logMessageTypeToLevel(type: LogMessageType): keyof LogLevels {
    switch (type) {
        case LogMessageType.Trace:
            return 'trace';
        case LogMessageType.Debug:
            return 'debug';
        case LogMessageType.Warn:
            return 'warn';
        case LogMessageType.Error:
            return 'error';
        case LogMessageType.Fatal:
            return 'fatal';
        case LogMessageType.Info:
        default:
            return 'info';
    }
}

export interface LoggerConfig {
    destinations: ILogDestination[];
    levels: LogLevels;
}

const DEFAULT_CONFIG: LoggerConfig = {
    destinations: [
        new ConsoleLogDestination()
    ],
    levels: {
        trace: true,
        debug: true,
        info: true,
        warn: true,
        error: true,
        fatal: true,
    }
};

export class Logger {
    private destinations: ILogDestination[];
    private levels: LogLevels;

    constructor(config = DEFAULT_CONFIG) {
        this.destinations = config.destinations;
        this.levels = config.levels;
    }

    trace(message?: any, uid?: string) {
        this.tryLog(LogMessageType.Debug, message, uid);
    }

    debug(message?: any, uid?: string) {
        this.tryLog(LogMessageType.Debug, message, uid);
    }

    info(message?: any, uid?: string) {
        this.tryLog(LogMessageType.Info, message, uid);
    }

    warn(message?: any, uid?: string) {
        this.tryLog(LogMessageType.Warn, message, uid);
    }

    error(message?: any, uid?: string) {
        this.tryLog(LogMessageType.Error, message, uid);
    }

    fatal(message?: any, uid?: string) {
        this.tryLog(LogMessageType.Error, message, uid);
    }

    private tryLog(type: LogMessageType, message: string, uid?: string) {
        const guard = logMessageTypeToLevel(type);
        if (!this.levels[guard]) {
            return;
        }

        this.destinations.forEach(dest => {
            if (!dest.canHandle(type)) {
                return;
            }

            try {
                dest.log(type, message, uid);
            } catch {
                /* Ignora errori del logger. */
            }
        });
    }
}
